import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, School, Users, Star, Eye, MessageCircle, Loader2 } from 'lucide-react';
import StarRating from './StarRating';
import ReviewsModal from './ReviewsModal';
import schoolStatsCache from '../utils/schoolStatsCache';
import studentCountCache from '../utils/studentCountCache';

const SchoolCard = ({ school, isSelected, onClick, onShowReview, refreshTrigger }) => {
    const [schoolStats, setSchoolStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showReviewsModal, setShowReviewsModal] = useState(false);
    const [lastFetchTime, setLastFetchTime] = useState(0);
    const [studentCount, setStudentCount] = useState(0);

    // Fetch school stats when component mounts
    useEffect(() => {
        if (school && school.id) {
            fetchSchoolStats();
            fetchStudentCount();
        }
    }, [school?.id]); // Only depend on school ID to prevent unnecessary re-fetches

    // Refresh stats when refreshTrigger changes (after review submission)
    useEffect(() => {
        if (school && school.id && refreshTrigger > 0) {
            console.log('SchoolCard: Refreshing stats due to refreshTrigger:', refreshTrigger);
            // Clear cache and refetch stats
            schoolStatsCache.clearSchool(school.id);
            setTimeout(() => {
                fetchSchoolStats();
            }, 500); // Small delay for refresh
        }
    }, [refreshTrigger]);

    const fetchSchoolStats = async () => {
        if (!school) return;

        // Prevent rapid successive calls (debounce)
        const now = Date.now();
        if (now - lastFetchTime < 1000) { // 1 second debounce
            return;
        }

        // Check cache first
        const cachedStats = schoolStatsCache.get(school.id);
        if (cachedStats) {
            setSchoolStats(cachedStats);
            return;
        }

        setLoading(true);
        setLastFetchTime(now);
        try {
            const response = await fetch(`/api/reviews/stats/${school.id}`);
            if (response.ok) {
                const stats = await response.json();
                setSchoolStats(stats);
                // Cache the result
                schoolStatsCache.set(school.id, stats);
            } else if (response.status === 429) {
                console.warn(`Rate limited for school ${school.id}, using default stats`);
                // Set default stats for rate limited requests
                const defaultStats = { averageRating: 0, reviewCount: 0, ratingDistribution: {} };
                setSchoolStats(defaultStats);
            }
        } catch (error) {
            console.error('Error fetching school stats:', error);
            // Set default stats on error
            setSchoolStats({ averageRating: 0, reviewCount: 0, ratingDistribution: {} });
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentCount = async () => {
        if (!school) return;

        try {
            const counts = await studentCountCache.getStudentCounts();
            const count = studentCountCache.getStudentCount(school.id);
            setStudentCount(count);
        } catch (error) {
            console.error('Error fetching student count:', error);
            setStudentCount(0);
        }
    };

    const handleViewReviews = (e) => {
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }
        setShowReviewsModal(true);
    };

    const handleWriteReview = (e) => {
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }
        onShowReview(school);
    };

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`bg-white rounded-none card-shadow border-2 transition-all duration-300 cursor-pointer hover:card-shadow-hover ${isSelected
                    ? 'border-primary-200 shadow-md bg-primary-50 school-card-expanded'
                    : 'border-gray-200 hover:border-primary-200'
                    }`}
                onClick={onClick}
            >
                {/* Main Card Content */}
                <div className="p-4">
                    {/* Header with School Name and Region */}
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center space-x-2 mb-1">
                                <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: getRegionColor(school.region) }}
                                />
                                <h3 className="font-bold text-lg text-gray-900 truncate flex-1" style={{ minWidth: 0, color: '#111827' }}>
                                    {school.name || 'Unknown School'}
                                </h3>
                            </div>

                            {/* School Type Badge - directly under name */}
                            <div className="mb-2">
                                <span
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                                    style={{ backgroundColor: getRegionColor(school.region) }}
                                >
                                    <School size={12} className="mr-1" />
                                    {school.type}
                                </span>
                            </div>

                            <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
                                <MapPin size={14} />
                                <span className="truncate">{school.district}, {school.region}</span>
                            </div>
                        </div>
                    </div>

                    {/* School Details */}
                    <div className="space-y-2 mb-4">
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                                <Users size={14} />
                                <span>{school.management}</span>
                            </div>
                            {school.classification && (
                                <div className="flex items-center space-x-1">
                                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                    <span>{school.classification}</span>
                                </div>
                            )}
                        </div>

                        {/* Student Count */}
                        <div className="flex items-center space-x-2 text-sm">
                            <div className="flex items-center space-x-1 text-primary-600">
                                <Users size={14} />
                                <span className="font-medium">{studentCount} students enrolled</span>
                            </div>
                        </div>

                        {school.address && (
                            <p className="text-sm text-gray-600 line-clamp-2">
                                {school.address}
                            </p>
                        )}
                    </div>

                    {/* Rating Section */}
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-gray-200 pt-4"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 size={20} className="animate-spin text-primary-600" />
                                <span className="ml-2 text-gray-600">Loading reviews...</span>
                            </div>
                        ) : schoolStats ? (
                            <div className="space-y-3">
                                {/* Rating Display */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-gray-900">
                                                {schoolStats.averageRating.toFixed(1)}
                                            </div>
                                            <StarRating
                                                rating={schoolStats.averageRating}
                                                size={16}
                                                showCount={true}
                                                reviewCount={schoolStats.reviewCount}
                                            />
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center space-x-2 action-buttons">
                                        {schoolStats.reviewCount > 0 && (
                                            <button
                                                onClick={handleViewReviews}
                                                className="flex items-center space-x-1 px-3 py-1.5 text-sm btn-secondary rounded-lg"
                                            >
                                                <Eye size={14} />
                                                <span>View Reviews</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={handleWriteReview}
                                            className="flex items-center space-x-1 px-3 py-1.5 text-sm btn-review text-white rounded-lg"
                                        >
                                            <MessageCircle size={14} />
                                            <span>Write Review</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Rating Distribution */}
                                {schoolStats.ratingDistribution && schoolStats.reviewCount > 0 && (
                                    <div className="space-y-1">
                                        {[5, 4, 3, 2, 1].map((rating) => {
                                            const count = schoolStats.ratingDistribution[rating] || 0;
                                            const percentage = (count / schoolStats.reviewCount) * 100;
                                            return (
                                                <div key={rating} className="flex items-center space-x-2">
                                                    <span className="text-sm text-gray-600 w-3">{rating}</span>
                                                    <Star size={12} className="text-yellow-400 fill-current" />
                                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-yellow-400 h-2 rounded-full rating-bar"
                                                            style={{ width: `${percentage}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-sm text-gray-600 w-8">{count}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <div className="text-gray-500 mb-2">No reviews yet</div>
                                <button
                                    onClick={handleWriteReview}
                                    className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors mx-auto"
                                >
                                    <MessageCircle size={14} />
                                    <span>Be the first to review</span>
                                </button>
                            </div>
                        )}
                    </motion.div>
                </div>
            </motion.div>

            {/* Reviews Modal */}
            <ReviewsModal
                school={school}
                isOpen={showReviewsModal}
                onClose={() => setShowReviewsModal(false)}
                refreshTrigger={refreshTrigger}
            />
        </>
    );
};

// Helper function to get region color
const getRegionColor = (region) => {
    const regionColors = {
        'Region 1': '#3B82F6',
        'Region 2E': '#10B981',
        'Region 2W': '#F59E0B',
        'Region 3': '#8B5CF6',
        'Region 4': '#EF4444',
        'Region 5N': '#06B6D4',
        'Region 5S': '#84CC16',
        'Greater Banjul Area': '#3B82F6',
        'West Coast Region': '#10B981',
        'North Bank Region': '#F59E0B',
        'Central River Region': '#8B5CF6',
        'Upper River Region': '#EF4444',
        'Lower River Region': '#06B6D4'
    };
    return regionColors[region] || '#6B7280';
};

export default SchoolCard;
